import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import styles from './manager.module.css';
import Loader from '../../components/Loader';
import back from '../../assets/icons/back.svg';
import CustomizedModal from '../../components/modal/CustomizedModal';
import { useNavigate } from "react-router-dom";
import { Box, Button, Paper, Table, TableHead, TableRow, TableCell, TableContainer } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

function ManageTable({ list, setList }) {
    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const newList = Array.from(list);
        const [movedItem] = newList.splice(result.source.index, 1);
        newList.splice(result.destination.index, 0, movedItem);

        // Update line_number based on new order
        newList.forEach((item, index) => {
            item.line_number = index + 1;
        });

        setList(newList);
    };

    return (
        <TableContainer component={Paper} elevation={0}>
            <Table>
                <TableHead>
                    <TableRow sx={{ background: '#c8ebea' }}>
                        <TableCell 
                            className={`${styles.head2Text} ${styles.headTable}`}
                            style={{ width: '60%', paddingLeft: '2.5pc' }}
                        >
                            ชื่อยศ
                        </TableCell> 
                        <TableCell 
                            className={`${styles.head2Text} ${styles.headTable}`}
                            style={{ width: '35%' }} 
                        >
                            ตัวย่อ
                        </TableCell> 
                        <TableCell 
                            className={`${styles.head2Text} ${styles.headTable}`}
                            style={{ width: '5%' }} 
                            align="center"
                        >
                        </TableCell> 
                    </TableRow>
                </TableHead>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="table-list">
                        {(provided) => (
                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                {list.map((each, index) => (
                                    <Draggable key={each.line_number} draggableId={each.line_number.toString()} index={index}>
                                        {(provided, snapshot) => (
                                            <TableRow
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                    background: snapshot.isDragging ? '#f0f0f0' : 'white',
                                                    cursor: 'grab',
                                                }}
                                            >
                                                <TableCell className={styles.bodyText} style={{ padding: '10px 8px 10px 40px' }}>
                                                    {each.name}
                                                </TableCell>
                                                <TableCell className={styles.bodyText} style={{ padding: '10px 8px 10px 15px' }}>
                                                    {each.remark}
                                                </TableCell>
                                                {!snapshot.isDragging && ( 
                                                    <TableCell sx={{ textAlign: "center", padding: "10px", boxSizing: "border-box" }}>
                                                        ☰
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </tbody>
                        )}
                    </Droppable>
                </DragDropContext>
            </Table>
        </TableContainer>
    );
}

function PositionOrderEdit() {
    const navigate = useNavigate();
    const [totalPage, setTotalPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [filteredList, setFilteredList] = useState([]);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [originalList, setOriginalList] = useState([]);

    useEffect(() => {
        fetchAllPositionData();
    }, []);

    const fetchAllPositionData = async (search) => {
        setLoading(true);
        try {
            // Fetch first page to determine total pages
            const firstRes = await api.findPosition(1, search, null);
            if (!firstRes || firstRes.data.code !== 200) {
                alert(firstRes?.data?.message || "ไม่ตอบสนอง");
                setLoading(false);
                return;
            }
    
            const totalPages = firstRes.data.pagging.totalPage;
            let allData = firstRes.data.data;
    
            if (totalPages > 1) {
                // Fetch remaining pages in parallel
                const requests = Array.from({ length: totalPages - 1 }, (_, i) => 
                    api.findPosition(i + 2, search, null)
                );
                
                const responses = await Promise.all(requests);
                
                // Extract valid data and merge
                allData = [
                    ...allData,
                    ...responses
                        .filter(res => res && res.data.code === 200)
                        .flatMap(res => res.data.data)
                ];
            }
    
            // Remove duplicates based on line_number (assuming it's unique)
            const uniqueData = Array.from(new Map(allData.map(item => [item.line_number, item])).values());
    
            setFilteredList(uniqueData);
            setTotalPage(totalPages);
            setOriginalList([...uniqueData]);
        } catch (error) {
            handleError("Error fetching data.");
        } finally {
            setLoading(false);
        }
    };
    
    const handlePrevious = () => {
        navigate('/positionManager');
    };

    const handleError = (msg) => {
        setErrorMessage(msg);
        setShowUpdateModal(false);
      };

    const handleUpdate = async () => {
        setShowUpdateModal(true);
      };

      const handleSave = async () => {
        setLoading(true);
        try {
            // Find only changed items
            const changedItems = filteredList.filter((item, index) => item.line_number !== originalList[index]?.line_number);
            
            if (changedItems.length === 0) {
                setLoading(false);
                return;
            }

            setShowUpdateModal(false);
            const batchSize = 5; // Adjust based on API limits
            for (let i = 0; i < changedItems.length; i += batchSize) {
                const batch = changedItems.slice(i, i + batchSize);
                await Promise.all(batch.map(item => api.updatePosition(item)));
            }
            
        } catch (error) {
            handleError("ไม่สามารถอัปเดตตำแหน่งได้");
        } finally {
            setLoading(false);
        }
    };
    
    const handleCloseModal = () => {
        setShowUpdateModal(false);
      };
    const handleCloseErrorModal = () => setErrorMessage(null);

    
    return (
        <>
            <Loader loading={loading} />
            {showUpdateModal && (
                <CustomizedModal
                  onClose={handleCloseModal}
                  apiCall={handleSave}
                  confirmMessage={`ยืนยันที่จะแก้ไขข้อมูล`}
                  confirmButtonText={`ยืนยัน`}
                />
              )}
              {errorMessage && (
                <CustomizedModal
                  onClose={handleCloseErrorModal}
                  confirmMessage={errorMessage}
                  apiCall={handleCloseErrorModal} // No action required, just closes
                  confirmButtonText={`ตกลง`}
                />
              )}
            <Box className={`${styles.bodyText} ${styles.inlineFlex}`} onClick={handlePrevious}>
                <img src={back} alt="Previous" className={styles.directIcon} />
                กลับไป จัดการยศ
            </Box>
            <Box className={styles.head1Text}>เรียงลำดับยศ</Box>

                <Box sx={{ border: '1.4px solid #478F96', borderRadius: 2, padding: 4, marginTop: '18px' }}>
                

                    {loading ? <p>กำลังโหลดข้อมูล...</p> : 
                    <Box sx={{  border: '1px solid #aeb4dd', borderRadius: 1}}>
                    <ManageTable list={filteredList} setList={setFilteredList} /></Box>}
                    </Box>

                

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, marginRight: 4.3 }}>
                <Button variant="contained" color="primary" onClick={handleUpdate} sx={{ padding: 1, borderRadius: 1.5 }}>
                    <p style={{ fontWeight: 600, margin: 0, fontSize: 12, padding: '0 40px' }}>บันทึก</p>
                </Button>
            </Box>
        </>
    );
}

export default PositionOrderEdit;
